import clsx from 'clsx';

import { ExpertRequestStage, ExpertRequestState } from '@/__generated__/graphql';

import { effectiveStage } from '../../utils/effectiveStage';
import { ER_STAGE_DOT, ER_STAGE_LABEL, ER_STAGE_PILL } from '../erStageTokens';

export interface ErStageBadgeProps {
  stage?: ExpertRequestStage | null;
  /** Dual-written legacy axis; drives the badge for null-stage rows via effectiveStage. */
  state?: ExpertRequestState | string | null;
  /** Pill size: small (list cards), default, or large (detail header). */
  size?: 'small' | 'default' | 'large';
  className?: string;
}

const SIZE_CLASS: Record<NonNullable<ErStageBadgeProps['size']>, string> = {
  small: 'text-xs py-[2px] pl-[6px] pr-[8px]',
  default: 'text-xs py-[3px] pl-[8px] pr-[10px]',
  large: 'text-sm py-[5px] pl-[10px] pr-[12px]',
};

/**
 * ErStageBadge — persistent lifecycle-stage indicator (ENG-5290 T2). Renders the
 * effective stage (null-stage legacy rows derived from `state`) as a soft-tint
 * pill with a colored dot. The SAME component is reused on project ER list cards.
 */
export function ErStageBadge({
  stage,
  state,
  size = 'default',
  className,
}: ErStageBadgeProps): JSX.Element {
  const resolved = effectiveStage(stage, state);
  return (
    <span
      data-testid="er-stage-badge"
      className={clsx(
        'inline-flex w-fit items-center gap-[6px] whitespace-nowrap rounded-full font-semibold',
        SIZE_CLASS[size],
        ER_STAGE_PILL[resolved],
        className
      )}
    >
      <span
        className={clsx('h-[7px] w-[7px] shrink-0 rounded-full', ER_STAGE_DOT[resolved])}
        aria-hidden="true"
      />
      {ER_STAGE_LABEL[resolved]}
    </span>
  );
}
